
import { useEffect, useState } from 'react';
import { ModuleTypeEnum } from '@scrambl/shared';
import { entityApi } from '@/lib/api/entities.api';
import { projectsApi } from '@/lib/api/projects.api';
import { isResolvedUrl } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useModuleLogo');

interface UseModuleLogoProps {
  moduleId?: string;
  moduleType: ModuleTypeEnum;
  logoUrl?: string | null; // S3 key or signed URL
}

// In-memory cache for signed logo URLs
// Key: `${moduleType}:${moduleId}` -> { signedUrl, timestamp }
const logoCache = new Map<string, { signedUrl: string | null; timestamp: number }>();
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes (signed URLs typically valid for 15-60 min)

// Pending requests to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<string | null>>();

// Batching: collect moduleIds per moduleType, flush after a short delay
const batchQueue = new Map<ModuleTypeEnum, Set<string>>();
const batchResolvers = new Map<string, { resolve: (url: string | null) => void; reject: (err: unknown) => void }[]>();
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY = 50; // ms to wait before flushing batch

function getCacheKey(moduleType: ModuleTypeEnum, moduleId: string): string {
  return `${moduleType}:${moduleId}`;
}

function flushBatch() {
  batchTimer = null;

  // Snapshot and clear the current queue
  const queues = new Map(batchQueue);
  batchQueue.clear();

  for (const [moduleType, moduleIds] of queues) {
    if (moduleIds.size === 0) continue;

    const ids = Array.from(moduleIds);

    // Create a single bulk request promise
    const bulkPromise = (async () => {
      let logoUrls: Record<string, string | null> = {};

      if (moduleType === ModuleTypeEnum.ENTITY) {
        logoUrls = await entityApi.bulkGetLogoUrls(ids);
      } else if (moduleType === ModuleTypeEnum.PROJECT) {
        logoUrls = await projectsApi.bulkGetLogoUrls(ids);
      }

      return logoUrls;
    })();

    // Resolve each individual pending request from the batch result
    bulkPromise
      .then((logoUrls) => {
        for (const id of ids) {
          const cacheKey = getCacheKey(moduleType, id);
          const signedUrl = logoUrls[id] ?? null;

          // Cache the result
          logoCache.set(cacheKey, { signedUrl, timestamp: Date.now() });

          // Resolve all waiting promises for this key
          const resolvers = batchResolvers.get(cacheKey);
          if (resolvers) {
            for (const { resolve } of resolvers) {
              resolve(signedUrl);
            }
            batchResolvers.delete(cacheKey);
          }

          pendingRequests.delete(cacheKey);
        }
      })
      .catch((error) => {
        for (const id of ids) {
          const cacheKey = getCacheKey(moduleType, id);

          const resolvers = batchResolvers.get(cacheKey);
          if (resolvers) {
            for (const { reject } of resolvers) {
              reject(error);
            }
            batchResolvers.delete(cacheKey);
          }

          pendingRequests.delete(cacheKey);
        }
      });
  }
}

function enqueueLogoRequest(moduleType: ModuleTypeEnum, moduleId: string): Promise<string | null> {
  const cacheKey = getCacheKey(moduleType, moduleId);

  // If there's already a pending request, just attach to it
  const existing = pendingRequests.get(cacheKey);
  if (existing) return existing;

  // Create a new promise and add to batch
  const promise = new Promise<string | null>((resolve, reject) => {
    const resolvers = batchResolvers.get(cacheKey) || [];
    resolvers.push({ resolve, reject });
    batchResolvers.set(cacheKey, resolvers);
  });

  pendingRequests.set(cacheKey, promise);

  // Add to batch queue
  if (!batchQueue.has(moduleType)) {
    batchQueue.set(moduleType, new Set());
  }
  batchQueue.get(moduleType)!.add(moduleId);

  // Schedule flush
  if (batchTimer === null) {
    batchTimer = setTimeout(flushBatch, BATCH_DELAY);
  }

  return promise;
}

/**
 * Hook to handle module logo URL conversion from S3 keys to signed URLs.
 * Automatically batches concurrent requests into bulk API calls to avoid rate limiting.
 */
export function useModuleLogo({ moduleId, moduleType, logoUrl }: UseModuleLogoProps) {
  const [signedLogoUrl, setSignedLogoUrl] = useState<string | null>(() => {
    // Initialize from cache if available
    if (!moduleId || !logoUrl) return null;
    const cacheKey = getCacheKey(moduleType, moduleId);
    const cached = logoCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.signedUrl;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSignedUrl = async () => {
      // Exit early if no data
      if (!moduleId || !logoUrl) {
        setSignedLogoUrl(null);
        return;
      }

      // Skip if logoUrl is already resolved (signed S3 URL, CDN URL, blob:, data:, or full HTTP URL)
      if (isResolvedUrl(logoUrl)) {
        setSignedLogoUrl(logoUrl);
        return;
      }

      // Only fetch for supported module types
      if (moduleType !== ModuleTypeEnum.ENTITY && moduleType !== ModuleTypeEnum.PROJECT) {
        // For other module types, use the logoUrl as is
        setSignedLogoUrl(logoUrl);
        return;
      }

      const cacheKey = getCacheKey(moduleType, moduleId);

      // Check cache first
      const cached = logoCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setSignedLogoUrl(cached.signedUrl);
        return;
      }

      try {
        setIsLoading(true);
        const signedUrl = await enqueueLogoRequest(moduleType, moduleId);
        if (!cancelled) {
          setSignedLogoUrl(signedUrl);
        }
      } catch (error) {
        logger.warn(`Failed to fetch signed URL for ${moduleType} logo:`, error);
        if (!cancelled) {
          setSignedLogoUrl(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSignedUrl();

    return () => {
      cancelled = true;
    };
  }, [moduleId, moduleType, logoUrl]);

  return {
    signedLogoUrl,
    isLoading
  };
}

/**
 * Invalidate cached logo for a specific module (call after logo upload/delete)
 */
export function invalidateModuleLogo(moduleType: ModuleTypeEnum, moduleId: string): void {
  // Remove all cache entries for this module
  const cacheKey = getCacheKey(moduleType, moduleId);
  logoCache.delete(cacheKey);
}
